/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IImageData, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ArrayExtensions, generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { INavigationMenuViewProps } from './herbal-content-navigation-menu';
import { IMenuItemData } from './herbal-content-navigation-menu.data';

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    categoryImage?: IImageData[] | null;
    mobileViewLabelText?: string;
    categoryImageAltText: string;
}

/**
 *
 * NavigationMenuView component.
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuViewProps, INavigationState> {
    private static isBackTrack: boolean = false;

    private currentLevel: number = 0;

    private readonly menuNode: React.RefObject<HTMLUListElement>;

    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    constructor(props: INavigationMenuViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.state = { activeMenu: undefined, mobileViewLabelText: '', parentMenu: undefined, categoryImageAltText: '' };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('scroll', this._handleScroll);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('scroll', this._handleScroll, false);
        }
    }

    public render(): JSX.Element | null {
        const {
            isMobileView,
            MenuList,
            MobileBackButton,
            MobileDescriptionContainer,
            MobileDescriptionLabel,
            Navigation,
            logoImage,
            logoImageContainer,
            Button
        } = this.props;
        const { leftMenuItemData, rightMenuItemData } = this.props;
        const { displayButtonOpenToggle } = this.props.config;
        this.currentLevel = 1;
        return (
            <Module
                {...Navigation}
                className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent')}
            >
                {displayButtonOpenToggle && (
                    <Node {...Button} onClick={this._handleButtonOpenToggle()}>
                        {'X'}
                    </Node>
                )}
                <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                    {isMobileView && this.state.activeMenu !== undefined && (
                        <Node {...MobileDescriptionContainer}>
                            <Node {...MobileBackButton} onClick={this._handleGoBack()} />
                            <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                        </Node>
                    )}
                    <li className='ms-main-menu_item left-section'>
                        <ul className='ms-nav__list'>{this._renderDisplay(leftMenuItemData)}</ul>
                    </li>
                    <li className='ms-main-menu_item logo'>
                        <Node {...logoImageContainer}>{logoImage}</Node>
                    </li>
                    <li className='ms-main-menu_item right-section'>
                        <ul className='ms-nav__list'>{this._renderDisplay(rightMenuItemData)}</ul>
                    </li>
                </Node>
            </Module>
        );
    }

    private _renderDisplay(menuItemData: IMenuItemData[]): JSX.Element[] {
        const { ListItem, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (const menuItemDatum of menuItemData) {
                if (menuItemDatum && menuItemDatum.id === activeMenu) {
                    menuItem = menuItemDatum;
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: undefined })
                        : this.setState({ parentMenu: undefined, mobileViewLabelText: menuItemDatum.linkText });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemDatum) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._createMenuItemList(menuItem)}{' '}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem}>
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    }

    private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: item?.id })
                        : this.setState({ parentMenu: item && item.id, mobileViewLabelText: subMenus[i].linkText });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        this.props.context.telemetry.error('Submenu content is empty, module wont render.');
        return null;
    }

    private _createMenuItemList(leftMenuItemData: IMenuItemData): JSX.Element | null {
        if (leftMenuItemData && leftMenuItemData.subMenu && leftMenuItemData.subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(leftMenuItemData.subMenu, leftMenuItemData.id, false);
            }
            return (
                <>
                    {this._renderButtonMenuItem(leftMenuItemData)}
                    {this._renderSubMenu(leftMenuItemData.subMenu, leftMenuItemData.id, false)}
                </>
            );
        } else if (leftMenuItemData && leftMenuItemData.linkText && leftMenuItemData.linkURL && leftMenuItemData.linkURL.length > 0) {
            return this._renderLinkMenuItem(leftMenuItemData, leftMenuItemData.id, false, true);
        } else if (leftMenuItemData && leftMenuItemData.linkText && !leftMenuItemData.linkURL) {
            return this._renderSpanMenuItem(leftMenuItemData, leftMenuItemData.id, true);
        }
        this.props.context.telemetry.error('Navigation menu content is empty, module wont render.');
        return null;
    }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean): JSX.Element | null {
        const { isMobileView, ListItem } = this.props;
        const enableMultiSupportMenu = this.props.config.enableMultilevelMenu || false;
        const multiLevelSupportedMenu = this.props.config.menuLevelSupport || 3;

        // Const isParentMenu:boolean= false;
        if (activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }

        let levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;
                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                }

                let subMenu;
                const haveSubmenu = hasOptions && enableMultiSupportMenu && this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
                if (haveSubmenu) {
                    this.currentLevel++;
                    levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                    subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true);
                }
                return (
                    <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });
        return this._renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu);
    }

    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <Node
                key={index}
                {...Button}
                onClick={this._handleDropdownToggle(option, activeMenu)}
                onFocus={this._closeSubmenu}
                aria-haspopup={!(this.state.activeMenu && this.state.activeMenu === option.id)}
                aria-expanded={!!(this.state.activeMenu && this.state.activeMenu === option.id)}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    private _renderLinkMenuItem(
        option: IMenuItemData,
        index?: number,
        hoverEffect: boolean = true,
        isParent: boolean = false
    ): JSX.Element | null {
        // @ts-ignore
        const { Link } = this.props;
        const { labelLoginButton } = this.props.config;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (option.linkText === labelLoginButton) {
            // @ts-ignore
            return <Node>{this.props.signInLink}</Node>;
        } else {
            return (
                <Node
                    {...Link}
                    key={index}
                    onFocus={isParent && this._closeSubmenu}
                    target={option.shouldOpenNewTab ? '_blank' : undefined}
                    onMouseOver={hoverEffect && this._updateCategoryImage(imagesource, option)}
                    href={option.linkURL}
                    {...attributes}
                    // onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
                    // @ts-ignore
                    onClick={e => {
                        !isParent && this._closeSubmenu(e, true);
                    }}
                >
                    {option.linkText}
                </Node>
            );
        }
    }

    private _renderPromotionalLink(linkText?: string, linkUrl?: string): JSX.Element | null {
        const { Link } = this.props;
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (linkText && linkUrl) {
            return (
                <Node {...Link} href={linkUrl} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}>
                    {linkText}
                </Node>
            );
        }
        return null;
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number, isParent: boolean = false): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span} onFocus={isParent && this._closeSubmenu}>
                {option.linkText}
            </Node>
        );
    }

    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, submenu?: boolean): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage, showPromotionalContent, isMobileView } = this.props;
        const categoryImageDisplay =
            !isMobileView &&
            (showCategoryImage || showPromotionalContent) &&
            !ArrayExtensions.hasElements(this.state.categoryImage) &&
            !submenu;
        const promotionalContentDisplay =
            !isMobileView && showPromotionalContent && ArrayExtensions.hasElements(this.state.categoryImage) && !submenu;
        const DivContainerClass = this.currentLevel > 2 || categoryImageDisplay ? DivContainer!.className : 'ms-nav__deafult';
        this.currentLevel = 1;
        return (
            <Node {...DivContainer} className={DivContainerClass}>
                <Node
                    {...MenuList}
                    className={classnames(
                        MenuList.className,
                        level,
                        categoryImageDisplay && 'havecateImage',
                        categoryImageDisplay &&
                            this.props.config.menuLevelSupport &&
                            this.props.config.menuLevelSupport > 2 &&
                            'navmenu-multi-level'
                    )}
                >
                    {menuOptions}
                    <span className='ms-sub-menu-close-btn' onClick={this._closeSubmenu}>
                        Close
                    </span>
                </Node>
                {categoryImageDisplay &&
                    this.state.categoryImage &&
                    this.state.categoryImage.map((item, index) => (
                        <Node key={index} {...ImageDivContainer} className={ImageDivContainer!.className}>
                            {this.state.categoryImage && this._getCategoryImage(item)}
                            {promotionalContentDisplay && this._renderPromotionalLink(item.altText, item.additionalProperties?.linkUrl)}
                        </Node>
                    ))}
            </Node>
        );
    }

    private readonly _updateCategoryImage = (categoryImageSrc: string, option: IMenuItemData) => () => {
        const linkText = option && option.linkText ? option.linkText : '';
        const promotionalImage: IImageData[] = [{ src: categoryImageSrc, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            option.promotionalContent.map(item => {
                if (item && item.image) {
                    const imageSrc = item.image.src;
                    const promotionalItemImageSettings = item.image.imageSettings;
                    promotionalImage.push({
                        src: imageSrc,
                        altText: item.text,
                        imageSettings: promotionalItemImageSettings,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                }
            });
        }
        this.setState({
            categoryImage: promotionalImage.length > 0 ? promotionalImage : [{ src: 'empty' }],
            categoryImageAltText: linkText
        });
    };

    private readonly _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        const overbody = [document.getElementsByTagName('body')];
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            NavigationMenuView.isBackTrack = false;
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }
        if (!this.state.activeMenu) {
            for (const ob of overbody) {
                ob[0].classList.add('overBody');
            }
        }
        for (const ob of overbody) {
            const hasFixed = ob[0].classList.contains('fixed');
            // const hasOver = ob[0].classList.contains('overBody');
            if (!this.state.activeMenu) {
                ob[0].classList.add('overBody');
                if (hasFixed) {
                    ob[0].classList.add('changeColorHeader');
                } else {
                    ob[0].classList.remove('changeColorHeader');
                }
            } else {
                ob[0].classList.remove('overBody');
                ob[0].classList.remove('changeColorHeader');
            }
        }
        this._resetFocus();
    };

    private readonly _handleGoBack = () => () => {
        NavigationMenuView.isBackTrack = true;
        this.setState({ activeMenu: this.state.parentMenu });

        this._resetFocus();
    };

    private readonly _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                if (this.menuNode && this.menuNode.current) {
                    this.menuNode.current.focus();
                }
            }, 0);
        }
    };

    /**
     * Method to handle scrolling.
     * @param event -HTML event.
     */
    private readonly _handleScroll = (event: Event) => {
        const elements = [
            document.getElementsByTagName('header'),
            document.getElementsByTagName('body'),
            document.getElementsByClassName('fixed-left'),
            document.getElementsByClassName('fixed-right')
        ];
        for (const el of elements) {
            this._handleFixedCss(el);
        }

        const hideElements = [document.getElementsByClassName('ms-intro-page')];
        for (const el of hideElements) {
            this._handleHideCss(el);
        }
    };

    /**
     * Method to handle adding fixed selector class.
     * @param el -any.
     */
    private readonly _handleFixedCss = (el: any) => {
        if (el.length) {
            if (window.pageYOffset > window.innerHeight) {
                el[0].classList.add('fixed');
            } else {
                el[0].classList.remove('fixed');
            }
        }
    };

    /**
     * Method to handle adding fixed selector class.
     * @param el -any.
     */
    private readonly _handleHideCss = (el: any) => {
        if (el.length) {
            if (window.pageYOffset > window.innerHeight) {
                el[0].classList.add('d-none');
                el[0].classList.remove('ms-intro-page');
            }
        }
    };

    private readonly _handleButtonOpenToggle = () => () => {
        const overheader = [document.getElementsByTagName('header')];
        for (const ob of overheader) {
            let headerIndex = 0;
            let menuIndex = 1;
            if (ob[0].children.length > 2) {
                headerIndex = 1;
                menuIndex = 2;
            }
            const hasFixed = ob[0].children[menuIndex].classList.contains('open');
            if (!hasFixed) {
                ob[0].children[headerIndex].classList.add('open');
                ob[0].children[menuIndex].classList.add('open');
            } else {
                ob[0].children[headerIndex].classList.remove('open');
                ob[0].children[menuIndex].classList.remove('open');
                const overbody = document.getElementsByTagName('body');
                overbody[0].classList.remove('overBody');
                overbody[0].classList.remove('changeColorHeader');
            }
        }
    };

    private _closeSubmenu(event: any, isFinalMenu?: boolean): void {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, categoryImage: null, mobileViewLabelText: '' });
        }
        const overbody = [document.getElementsByTagName('body')];
        for (const ob of overbody) {
            ob[0].classList.remove('overBody');
            ob[0].classList.remove('changeColorHeader');
        }
        if (isFinalMenu) {
            const overheader = [document.getElementsByTagName('header')];
            for (const ob of overheader) {
                const hasFixed = ob[0].children[0].classList.contains('open');
                if (!hasFixed) {
                    ob[0].children[0].classList.add('open');
                    ob[0].children[1].classList.add('open');
                } else {
                    ob[0].children[0].classList.remove('open');
                    ob[0].children[1].classList.remove('open');
                }
            }
        }
    }

    private readonly _getCategoryImage = (categoryImage?: IImageData): React.ReactNode | null => {
        if (!categoryImage || !categoryImage.src) {
            return null;
        }

        const categoryImageUrl = generateImageUrl(`${categoryImage.src}`, this.props.context.actionContext.requestContext.apiSettings);
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                sm: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                md: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                lg: { q: 'w=300&h=250&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };
        if (categoryImageUrl !== undefined) {
            return (
                // @ts-ignore
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-nav-image__item'
                    {...categoryImage}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={categoryImage.imageSettings ?? this.props.config.categoryImageSettings ?? defaultImageSettings}
                    loadFailureBehavior='hide'
                    role='tabpanel'
                    id='categoryImageTag__categoryImage'
                    altText={categoryImage.altText}
                />
            );
        }
        return null;
    };
}

export default NavigationMenuView;
